import _ from 'lodash'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { createColumnHelper } from '@tanstack/react-table'

import { IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch } from '@mui/material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SettingsIcon from '@mui/icons-material/Settings'

import Table, { TableProps } from '@/components/ui/tables/Table'

export type PolicyData = {
  id: string
  name: string
  description?: string
  canDelete?: boolean
}

export type PoliciesTableProps = Pick<TableProps<PolicyData>, 'disableGlobalSearch' | 'selectable'> & {
  data: PolicyData[]
  disableActions?: boolean
  loading?: boolean
  onDeleteClick?: (id: string) => void
}

const columnHelper = createColumnHelper<PolicyData>()

export function PoliciesTable(props: PoliciesTableProps) {
  const { data, disableActions = false, disableGlobalSearch = false, loading, onDeleteClick, selectable = true } = props

  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return _.compact([
      columnHelper.accessor('name', {
        header: t('table.policies.header.name').toString(),
      }),
      columnHelper.accessor('description', {
        header: t('table.policies.header.description').toString(),
      }),
      !disableActions &&
        columnHelper.display({
          id: 'actions',
          header: ({ table }) => (
            <Stack alignItems="center" justifyContent="center">
              <PopupState variant="popover" popupId="groups-table-configuration">
                {(popupState) => (
                  <>
                    <IconButton aria-label="configure columns" {...bindTrigger(popupState)}>
                      <SettingsIcon />
                    </IconButton>
                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <List>
                        {table
                          .getAllLeafColumns()
                          .filter((column) => column.id !== 'actions')
                          .map((column) => (
                            <ListItem key={column.id}>
                              <ListItemText id={column.id} primary={column.columnDef.header} />
                              <Switch edge="end" onChange={column.getToggleVisibilityHandler()} checked={column.getIsVisible()} />
                            </ListItem>
                          ))}
                      </List>
                    </Menu>
                  </>
                )}
              </PopupState>
            </Stack>
          ),
          cell: ({ row, table }) => (
            <Stack justifyContent="flex-end" direction="row" spacing={1}>
              <PopupState variant="popover" popupId={`group-${row.id}-actions`}>
                {(popupState) => (
                  <>
                    <IconButton aria-label="policy actions" {...bindTrigger(popupState)}>
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem
                        onClick={() => {
                          table.options.meta?.open?.(row.original.id)
                        }}
                      >
                        <ListItemIcon>
                          <LaunchIcon />
                        </ListItemIcon>
                        <ListItemText>{t('common:button.details.label')}</ListItemText>
                      </MenuItem>
                      {row.original.canDelete && (
                        <MenuItem
                          onClick={() => {
                            table.options.meta?.delete?.(row.original.id)
                            popupState.close()
                          }}
                        >
                          <ListItemIcon>
                            <DeleteRoundedIcon />
                          </ListItemIcon>
                          <ListItemText>{t('common:button.delete.label')}</ListItemText>
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </PopupState>
            </Stack>
          ),
          meta: {
            align: 'center',
            sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
            sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
          },
        }),
    ])
  }, [disableActions, t])

  return (
    <Table
      columns={columns}
      data={data}
      disableGlobalSearch={disableGlobalSearch}
      loading={loading}
      meta={{
        open: (policyId: string) => {
          navigate(`${location.pathname}/${policyId}`)
        },
        delete: onDeleteClick,
      }}
      selectable={selectable}
    />
  )
}

export default PoliciesTable
